import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ThemeMode,
  AccentColor,
  DensityMode,
  WallpaperId,
  PresetTheme,
  EdgeMailAppearancePreferences,
} from '@/types/appearance';

// Settings categories that mirror Outlook's structure
export type SettingsMainCategory = 'accounts' | 'files' | 'general' | 'mail' | 'calendar' | 'people';

// Subcategories under "General" (matching Outlook)
export type GeneralSubcategory =
  | 'language-and-time'
  | 'appearance'
  | 'notifications'
  | 'accessibility'
  | 'privacy-and-data'
  | 'search'
  | 'reliability'
  | 'startup'
  | 'about';

// Appearance preferences
export type AppearancePreferences = EdgeMailAppearancePreferences;

// Notification preferences. This mirrors the backend
// EdgeMailUserPreferences NotificationPreferences contract.
export type MailNotificationDetailLevel = 'private' | 'sender' | 'sender_subject';
export type TaskPushMinPriority = 'low' | 'medium' | 'high' | 'urgent';
export type MailPushOtherMode = 'off' | 'quiet' | 'normal';
export type MailPushMatterLevel = 'normal' | 'time_sensitive';

export interface NotificationPreferences {
  // Master toggle
  notificationsEnabled: boolean;

  // Mail notifications
  mailNotifications: boolean;
  mailNewEmail: boolean;
  mailForegroundBanner: boolean;
  mailPushNotifications: boolean;
  mailNotificationDetailLevel: MailNotificationDetailLevel;
  // Mobile push policy (decided per message by the server; 2026-09-05 plan)
  mailPushOtherMode: MailPushOtherMode;
  mailPushKnownSendersOnly: boolean;
  mailPushVipSenders: string[];
  mailPushMatterLevel: MailPushMatterLevel;
  mailPushMaxAgeMinutes: number;
  mailMentions: boolean;

  // Calendar notifications
  calendarNotifications: boolean;
  calendarEventReminders: boolean;

  // Document notifications
  documentsNotifications: boolean;
  documentsComments: boolean;
  documentsEdits: boolean;
  documentsMentions: boolean;

  // Task mobile-push controls
  tasksPushNotifications: boolean;
  tasksPushOnAssignment: boolean;
  tasksPushOnReminder: boolean;
  tasksPushMinPriority: TaskPushMinPriority;
  /** Tier 3 (2026-09-09): pause every Connect push between start and end in the user's timezone. */
  connectQuietHours?: { enabled: boolean; start: string; end: string; timezone: string } | null;
}

export interface SettingsState {
  // Modal state
  isOpen: boolean;

  // Navigation state
  activeMainCategory: SettingsMainCategory;
  activeSubcategory: string;

  // Appearance preferences
  appearancePreferences: AppearancePreferences;

  // Notification preferences
  notificationPreferences: NotificationPreferences;

  // Actions
  openSettings: () => void;
  closeSettings: () => void;
  setActiveMainCategory: (category: SettingsMainCategory) => void;
  setActiveSubcategory: (subcategory: string) => void;
  setAppearancePreferences: (prefs: Partial<AppearancePreferences>) => void;
  setNotificationPreferences: (prefs: Partial<NotificationPreferences>) => void;
}

const DEFAULT_APPEARANCE_PREFERENCES: AppearancePreferences = {
  themeMode: 'dark',
  accentColor: 'blue',
  accentCustomHex: null,
  neutralBase: 'graphite',
  wallpaper: 'night-mountain',
  wallpaperCustom: null,
  presetTheme: 'classic',
  wallpaperDim: 0.2,
  wallpaperBlur: 0,
  density: 'cozy',
  fontScale: 1,
  contrastMode: 'standard',
  mailTextPrimaryColor: null,
  mailTextSecondaryColor: null,
  mailTextDefaultPrimaryColor: null,
  mailTextDefaultSecondaryColor: null,
  mailTextPrimaryColorForce: false,
  mailTextSecondaryColorForce: false,
  mailSidebarTextColor: null,
  mailSidebarTextDefaultColor: null,
  mailSidebarTextColorForce: false,
};

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  notificationsEnabled: true,
  mailNotifications: true,
  mailNewEmail: true,
  mailForegroundBanner: true,
  mailPushNotifications: false,
  mailNotificationDetailLevel: 'private',
  mailPushOtherMode: 'quiet',
  mailPushKnownSendersOnly: false,
  mailPushVipSenders: [],
  mailPushMatterLevel: 'normal',
  mailPushMaxAgeMinutes: 15,
  mailMentions: true,
  calendarNotifications: true,
  calendarEventReminders: true,
  documentsNotifications: true,
  documentsComments: true,
  documentsEdits: true,
  documentsMentions: true,
  tasksPushNotifications: true,
  tasksPushOnAssignment: true,
  tasksPushOnReminder: true,
  tasksPushMinPriority: 'low',
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // Modal state
      isOpen: false,

      // Default to General > Appearance (since we're implementing theming)
      activeMainCategory: 'general',
      activeSubcategory: 'appearance',

      // Appearance preferences
      appearancePreferences: DEFAULT_APPEARANCE_PREFERENCES,

      // Notification preferences
      notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,

      // Actions
      openSettings: () => set({ isOpen: true }),
      closeSettings: () => set({ isOpen: false }),

      setActiveMainCategory: (category) => set(() => {
        // When switching main categories, set a default subcategory
        const defaultSubcategories: Record<SettingsMainCategory, string> = {
          accounts: 'email-accounts',
          files: 'attachments',
          general: 'appearance',
          mail: 'compose',
          calendar: 'events',
          people: 'contacts',
        };

        return {
          activeMainCategory: category,
          activeSubcategory: defaultSubcategories[category],
        };
      }),

      setActiveSubcategory: (subcategory) => set({ activeSubcategory: subcategory }),

      setAppearancePreferences: (prefs) => set((state) => ({
        appearancePreferences: {
          ...state.appearancePreferences,
          ...prefs,
        },
      })),

      setNotificationPreferences: (prefs) => set((state) => ({
        notificationPreferences: {
          ...state.notificationPreferences,
          ...prefs,
        },
      })),
    }),
    {
      name: 'edgemail-settings',
      // Only persist preferences, not modal/navigation state
      partialize: (state) => ({
        appearancePreferences: state.appearancePreferences,
        notificationPreferences: state.notificationPreferences,
      }),
      // Merge persisted state with defaults to handle new properties
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<SettingsState> | undefined;
        return {
          ...currentState,
          appearancePreferences: {
            ...DEFAULT_APPEARANCE_PREFERENCES,
            ...(persisted?.appearancePreferences || {}),
          },
          notificationPreferences: {
            ...DEFAULT_NOTIFICATION_PREFERENCES,
            ...(persisted?.notificationPreferences || {}),
          },
        };
      },
    }
  )
);
